import { useEffect, useRef, useState } from "react";
import { Inbox, Paperclip, Send, Store, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatChatDate } from "@/lib/format";
import { useLanguage } from "@/i18n/LanguageContext";

const fileUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export const AdminMessages = ({ onRead }) => {
  const { t, lang } = useLanguage();
  const [convs, setConvs] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadList = () =>
    api.get("/admin/conversations").then((r) => {
      setConvs(r.data);
      return r.data;
    }).catch(() => setConvs([]));

  const openConversation = (id) => {
    setActiveId(id);
    api.get(`/admin/conversations/${id}`).then((r) => {
      setThread(r.data);
      loadList();
      onRead?.();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }).catch(() => toast.error(t("Impossible de charger la conversation")));
  };

  useEffect(() => {
    loadList().then((list) => {
      const alreadyRead = list?.find((c) => c.unread_admin === 0);
      if (alreadyRead) openConversation(alreadyRead.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    if (!text.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/admin/conversations/${activeId}/messages`, { text });
      setText("");
      openConversation(activeId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (convs === null) {
    return <div className="mt-6 h-96 animate-pulse rounded-3xl bg-white" />;
  }

  if (convs.length === 0) {
    return (
      <div className="mt-6 rounded-3xl border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="admin-messages-empty">
        <Inbox className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-3 text-sm font-semibold text-slate-700">{t("Aucun message pour le moment")}</p>
        <p className="mt-1 text-xs text-slate-400">{t("Les demandes de support des propriétaires Pro apparaîtront ici.")}</p>
      </div>
    );
  }

  const active = convs.find((c) => c.id === activeId);

  return (
    <div className="mt-6 grid gap-5 lg:grid-cols-[340px_1fr]" data-testid="admin-messages">
      {/* LISTE DES CONVERSATIONS */}
      <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
        <p className="border-b border-slate-100 px-5 py-4 text-sm font-bold text-slate-900">
          {t("Boîte de réception")}
          <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{convs.length}</span>
        </p>
        <div className="max-h-[520px] divide-y divide-slate-50 overflow-y-auto" data-testid="admin-conv-list">
          {convs.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`admin-conv-${c.id}`}
              onClick={() => openConversation(c.id)}
              className={`flex w-full items-start gap-3 px-4 py-3.5 text-left transition ${activeId === c.id ? "bg-[#EFF6FF]" : "hover:bg-slate-50"}`}
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${activeId === c.id ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-500"}`}>
                {c.user_name?.[0]?.toUpperCase() || "P"}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-slate-900">{c.subject}</span>
                  {c.unread_admin > 0 && (
                    <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[#2563EB] px-1 text-[10px] font-bold text-white" data-testid={`admin-conv-unread-${c.id}`}>
                      {c.unread_admin}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-xs text-slate-500">
                  {c.user_name}{c.establishment_name ? ` • ${c.establishment_name}` : ""}
                </span>
                <span className="mt-0.5 block truncate text-xs text-slate-400">{c.last_message?.text}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* FIL DE DISCUSSION */}
      {!active ? (
        <div className="flex min-h-[480px] items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="admin-thread-placeholder">
          <div>
            <Inbox className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">{t("Sélectionne une conversation pour lire les messages.")}</p>
          </div>
        </div>
      ) : (
      <div className="flex min-h-[480px] flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm" data-testid="admin-thread">
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EFF6FF] text-sm font-bold text-[#2563EB]">
            {active.user_name?.[0]?.toUpperCase() || "P"}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900">{active.subject}</p>
            <p className="flex items-center gap-2 text-xs text-slate-400">
              <User className="h-3 w-3" />
              {active.user_name}
              <span className="rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[9px] font-bold text-[#2563EB]">{active.category || "Question"}</span>
              {active.establishment_name && (
                <span className="flex items-center gap-1">
                  <Store className="h-3 w-3" />
                  {active.establishment_name}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50/60 p-5" data-testid="admin-thread-messages">
          {thread?.messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[75%] rounded-3xl px-4 py-3 ${
                  m.sender_role === "admin"
                    ? "rounded-br-md bg-[#2563EB] text-white"
                    : "rounded-bl-md border border-slate-100 bg-white text-slate-800"
                }`}
              >
                <p className={`text-[10px] font-bold ${m.sender_role === "admin" ? "text-blue-200" : "text-slate-400"}`}>
                  {m.sender_role === "admin" ? "Toi (Admin)" : m.sender_name} • {formatChatDate(m.created_at, lang)}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{m.text}</p>
                {m.attachment_url && (
                  <a
                    data-testid={`admin-attachment-${m.id}`}
                    href={fileUrl(m.attachment_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1.5 flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold underline-offset-2 hover:underline"
                    style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
                  >
                    <Paperclip className="h-3 w-3" />
                    {m.attachment_name || "Pièce jointe"}
                  </a>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-slate-100 p-4">
          <div className="flex items-end gap-2.5">
            <textarea
              data-testid="admin-reply-input"
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={t("Écris ta réponse au propriétaire…")}
              className="min-w-0 flex-1 resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100"
            />
            <button
              type="button"
              data-testid="admin-reply-send"
              onClick={send}
              disabled={sending || !text.trim()}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2563EB] text-white shadow-sm transition hover:bg-[#1D4ED8] disabled:opacity-40"
              aria-label={t("Envoyer")}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};
